import * as React from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { ML_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import type { SearchWithinType } from '@/features/ChatBotV2/context';
import type { SessionState } from '@/features/ChatBotV2/types';

import { QUERY_KEY } from './useFetchAllSession';
import { QUERY_KEY as SESSION_BY_ID_QUERY_KEY } from './useFetchSessionById';

export const useCreateUpdateSession = () => {
  const invalidateSession = useInvalidateSession();
  const invalidateAllSessionsInfos = useInvalidateAllSessionsInfos();

  return useMutation(
    async ({
      currentFeedOrDocumentId,
      searchWithin,
      sessionState,
    }: UseCreateUpdateSessionType) => {
      await axiosInstance.put(
        ML_API_ROUTES.askNeedlSessionById(sessionState.sessionId as string),
        {
          title: sessionState.messages[0].prompt,
          state: {
            currentFeedOrDocumentId: currentFeedOrDocumentId,
            searchWithin: searchWithin,
            messages: sessionState.messages,
            attachments: sessionState.attachments,
            attachmentsFeedId: sessionState.attachmentsFeedId,
            metadata: sessionState.metadata,
            settings: {
              pro: !!sessionState.pro,
            },
          },
        }
      );
    },
    {
      onSuccess: async (_, { sessionState }) => {
        await Promise.all([
          invalidateAllSessionsInfos(),
          invalidateSession(sessionState.sessionId as string),
        ]);
      },
    }
  );
};

export const useInvalidateSession = () => {
  const queryClient = useQueryClient();

  return React.useCallback(
    (sessionId: string) => {
      return queryClient.invalidateQueries(
        [SESSION_BY_ID_QUERY_KEY, sessionId],
        {
          refetchInactive: true,
        }
      );
    },
    [queryClient]
  );
};

export const useInvalidateAllSessionsInfos = () => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    return queryClient.invalidateQueries([QUERY_KEY], {
      refetchInactive: true,
    });
  }, [queryClient]);
};

type UseCreateUpdateSessionType = {
  currentFeedOrDocumentId: string;
  searchWithin: SearchWithinType;
  sessionState: SessionState;
};
